import {
  PlusIcon,
  XCircleIcon,
  Cog6ToothIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CursorArrowRippleIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import CompactPagination from '../components/CompactPagination'
import AssignUsersModal from '../components/projects/AssignUsersModal'
import BatchExportModal from '../components/projects/BatchExportModal'
import ProjectModal from '../components/projects/ProjectModal'
import StatusBadge from '../components/projects/StatusBadge'
import { useAuth } from '../contexts/AuthContext'
import { projectAPI } from '../services/api'
import { ProjectWithStats, ProjectsResponse, ProjectStatus } from '../types/index'
import { PROJECT_STATUS_CONFIG } from '../utils/projectStatus'

type SortColumn =
  | 'completed'
  | 'name'
  | 'images'
  | 'progress'
  | 'qualified'
  | 'status'
  | 'qa_pending'
type SortDirection = 'asc' | 'desc' | 'default'

interface SortState {
  column: SortColumn | null
  direction: SortDirection
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [projectsData, setProjectsData] = useState<ProjectsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showBatchExportModal, setShowBatchExportModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignModalProject, setAssignModalProject] = useState<ProjectWithStats | null>(null)
  const [error, setError] = useState<string | null>(null)

  // My Projects state (assigned projects only)
  const [myProjectsData, setMyProjectsData] = useState<ProjectsResponse | null>(null)
  const [myProjectsLoading, setMyProjectsLoading] = useState(true)

  // Selection state
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set())
  const [selectMode, setSelectMode] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 15

  // Load saved preferences from localStorage
  const [searchQuery, setSearchQuery] = useState(
    () => localStorage.getItem('projectsSearchQuery') || ''
  )
  const [sortState, setSortState] = useState<SortState>(() => {
    const saved = localStorage.getItem('projectsSortState')
    return saved ? JSON.parse(saved) : { column: null, direction: 'default' }
  })
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>(
    () => (localStorage.getItem('projectsStatusFilter') as ProjectStatus) || 'all'
  )

  useEffect(() => {
    fetchProjectsData()
  }, [currentPage, searchQuery, sortState, statusFilter])

  useEffect(() => {
    fetchMyProjectsData()
  }, [searchQuery, sortState, statusFilter])

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('projectsSearchQuery', searchQuery)
  }, [searchQuery])

  useEffect(() => {
    localStorage.setItem('projectsSortState', JSON.stringify(sortState))
  }, [sortState])

  // For admin users, default sort to pending count (desc) if no explicit preference
  useEffect(() => {
    if (user?.is_admin) {
      const saved = localStorage.getItem('projectsSortState')
      const savedState: SortState | null = saved ? JSON.parse(saved) : null
      const isDefault =
        !savedState || savedState.direction === 'default' || savedState.column === null
      if (isDefault && !(sortState.column === 'qa_pending' && sortState.direction === 'desc')) {
        setSortState({ column: 'qa_pending', direction: 'desc' })
      }
    }
  }, [user])

  useEffect(() => {
    localStorage.setItem('projectsStatusFilter', statusFilter)
  }, [statusFilter])

  const fetchProjectsData = async () => {
    try {
      setLoading(true)
      setError(null)

      const apiParams = {
        page: currentPage,
        page_size: pageSize,
        search: searchQuery?.trim() || undefined,
        status_filter: statusFilter !== 'all' ? statusFilter : undefined,
        sort_by: sortState.column || undefined,
        sort_order: sortState.direction,
      }

      const response = await projectAPI.listPaginated(apiParams)

      setProjectsData(response.data)
    } catch (err) {
      console.error('Error fetching projects:', err)
      setError('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  const fetchMyProjectsData = async () => {
    try {
      setMyProjectsLoading(true)
      const apiParams = {
        page: 1,
        page_size: 20,
        search: searchQuery?.trim() || undefined,
        status_filter: statusFilter !== 'all' ? statusFilter : undefined,
        sort_by: sortState.column || undefined,
        sort_order: sortState.direction,
        assigned_only: true,
      }
      const response = await projectAPI.listPaginated(apiParams)
      setMyProjectsData(response.data)
    } catch (err) {
      console.error('Error fetching my projects:', err)
      setMyProjectsData(null)
    } finally {
      setMyProjectsLoading(false)
    }
  }

  const handleCreateProject = async (projectData: Partial<ProjectWithStats>) => {
    try {
      await projectAPI.create(projectData)
      setCurrentPage(1) // Reset to first page
      await fetchProjectsData()
      setShowCreateModal(false)
    } catch (err: any) {
      console.error('Error creating project:', err)
      const errorMessage = err.response?.data?.detail || 'Failed to create project'
      alert(errorMessage)
    }
  }

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (
      confirm(
        `Are you sure you want to delete "${projectName}"? This will delete all associated images and annotations.`
      )
    ) {
      try {
        await projectAPI.delete(projectId)
        // If we're on the last page and it becomes empty, go to previous page
        if (projectsData && projectsData.items.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        } else {
          await fetchProjectsData()
        }
        // Remove from selection
        setSelectedProjects(prev => {
          const newSet = new Set(prev)
          newSet.delete(projectId)
          return newSet
        })
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || 'Failed to delete project'
        alert(errorMessage)
      }
    }
  }

  const handleColumnSort = (column: SortColumn) => {
    if (sortState.column === column) {
      // Cycle through: asc -> desc -> default
      const nextDirection: SortDirection =
        sortState.direction === 'asc' ? 'desc' : sortState.direction === 'desc' ? 'default' : 'asc'

      const newSortState: SortState = {
        column: nextDirection === 'default' ? null : column,
        direction: nextDirection,
      }

      setSortState(newSortState)
    } else {
      // First click on a new column
      const newSortState: SortState = { column, direction: 'asc' }
      setSortState(newSortState)
    }
    // Reset to first page when sorting changes
    setCurrentPage(1)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleStatusFilter = (status: ProjectStatus | 'all') => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Get projects (no client-side filtering needed as backend handles it)
  const getDisplayedProjects = () => {
    if (!projectsData) return []
    return projectsData.items
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Selection handlers
  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  const selectAllOnPage = () => {
    const displayedProjects = getDisplayedProjects()
    if (displayedProjects.length === 0) return

    const allSelected = displayedProjects.every(p => selectedProjects.has(p.id))

    if (allSelected) {
      // Deselect all on page
      setSelectedProjects(prev => {
        const newSet = new Set(prev)
        displayedProjects.forEach(p => newSet.delete(p.id))
        return newSet
      })
    } else {
      // Select all on page
      setSelectedProjects(prev => {
        const newSet = new Set(prev)
        displayedProjects.forEach(p => newSet.add(p.id))
        return newSet
      })
    }
  }

  const selectAllQualified = async () => {
    try {
      // Fetch all qualified project IDs from backend
      const response = await projectAPI.getQualifiedProjectIds()
      setSelectedProjects(new Set(response.data))
      setSelectMode(true)
    } catch (err) {
      console.error('Error fetching qualified projects:', err)
      alert('Failed to select qualified projects')
    }
  }

  const clearSelection = () => {
    setSelectedProjects(new Set())
  }

  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedProjects(new Set())
      setSelectMode(false)
    } else {
      setSelectMode(true)
    }
  }

  const isAllOnPageSelected = () => {
    const displayedProjects = getDisplayedProjects()
    if (displayedProjects.length === 0) return false
    return displayedProjects.every(p => selectedProjects.has(p.id))
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-2">Manage your annotation projects and track progress</p>
        </div>
        <div className="flex items-center space-x-3">
          {user?.is_admin && (
            <>
              <button
                className={`${selectMode ? 'btn-secondary' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md'} flex items-center`}
                onClick={toggleSelectMode}
              >
                {selectMode ? (
                  <>
                    <XCircleIcon className="w-5 h-5 mr-2" />
                    Cancel Selection
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                    Export
                  </>
                )}
              </button>
              <button
                className="btn-primary flex items-center"
                onClick={() => setShowCreateModal(true)}
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                New Project
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters - Status dropdown and Search at top */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {/* Status Filter */}
          <div className="min-w-0">
            <select
              value={statusFilter}
              onChange={e => handleStatusFilter(e.target.value as ProjectStatus | 'all')}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
            >
              <option value="all">All Status</option>
              {Object.entries(PROJECT_STATUS_CONFIG).map(([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="relative max-w-md flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by project name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectMode && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 py-2">
              <span className="text-sm font-medium text-blue-900">
                {selectedProjects.size} project{selectedProjects.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={selectAllOnPage}
                className="text-sm text-blue-700 hover:text-blue-900 underline"
              >
                {isAllOnPageSelected() ? 'Deselect' : 'Select'} all on page
              </button>
              {user?.is_admin && (
                <button
                  onClick={selectAllQualified}
                  className="text-sm text-blue-700 hover:text-blue-900 underline"
                >
                  Select all qualified
                </button>
              )}
              {selectedProjects.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-blue-700 hover:text-blue-900 underline"
                >
                  Clear selection
                </button>
              )}
            </div>
            {selectedProjects.size > 0 && (
              <button
                onClick={() => setShowBatchExportModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
              >
                Export Selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* My Projects Section - when user has assigned projects */}
      {myProjectsData && myProjectsData.items.length > 0 && (
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">My Projects</h2>
          <p className="text-sm text-gray-600 mb-4">Projects you have been assigned to</p>
          {myProjectsLoading ? (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Project Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Images
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Your Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Qualified
                    </th>
                    {user?.is_admin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Pending
                      </th>
                    )}
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {myProjectsData.items.map(project => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          status={project.user_status?.status || ProjectStatus.NOT_STARTED}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {project.is_user_completed ? (
                          <CheckCircleIconSolid className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                        {project.description && (
                          <div className="text-sm text-gray-500">{project.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {project.total_images}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">
                            {project.annotated_images_by_user} / {project.total_images}
                          </span>
                          <div className="w-16 h-2 rounded bg-gray-200 overflow-hidden">
                            <div
                              className="h-full bg-blue-500"
                              style={{
                                width: `${project.total_images > 0 ? (project.annotated_images_by_user / project.total_images) * 100 : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {project.is_qualified ? (
                          <CheckCircleIconSolid className="w-5 h-5 text-green-600 inline" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-gray-400 inline" />
                        )}
                        <span className="ml-1 text-sm">{project.completed_by_count || 0} / 3</span>
                      </td>
                      {user?.is_admin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {project.qa_pending_user_count ?? 0}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/projects/${project.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Open project"
                          >
                            <CursorArrowRippleIcon className="w-5 h-5" />
                          </button>
                          {user?.is_admin && (
                            <>
                              <button
                                onClick={() => {
                                  setAssignModalProject(project)
                                  setShowAssignModal(true)
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Assign users"
                              >
                                <UserPlusIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/analysis`)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Analyze project"
                              >
                                <ChartBarIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/review`)}
                                className="text-purple-600 hover:text-purple-900"
                                title="Review annotations"
                              >
                                <ClipboardDocumentCheckIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/edit`)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Edit project"
                              >
                                <Cog6ToothIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProject(project.id, project.name)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete project"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* All Projects Section */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Projects</h2>

        {/* Pagination - for All Projects table */}
        {projectsData && projectsData.total_pages > 1 && (
          <div className="mb-4">
            <CompactPagination
              currentPage={currentPage}
              totalPages={projectsData.total_pages}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              totalItems={projectsData.total}
            />
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading projects...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button className="btn-primary" onClick={fetchProjectsData}>
              Retry
            </button>
          </div>
        ) : !projectsData || getDisplayedProjects().length === 0 ? (
          <div className="text-center py-12">
            {searchQuery || statusFilter !== 'all' ? (
              <>
                <MagnifyingGlassIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600">
                  {searchQuery && statusFilter !== 'all'
                    ? 'Try adjusting your search query or status filter'
                    : searchQuery
                      ? 'Try adjusting your search query'
                      : 'Try adjusting your status filter'}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
                <p className="text-gray-600 mb-6">
                  {user?.is_admin
                    ? 'Get started by creating your first annotation project'
                    : 'No projects available yet. Please contact an administrator.'}
                </p>
                {user?.is_admin && (
                  <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                    Create Project
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {selectMode && (
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={isAllOnPageSelected()}
                        onChange={selectAllOnPage}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('status')}
                  >
                    <div className="flex items-center">
                      <span>Status</span>
                      {sortState.column === 'status' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('completed')}
                  >
                    <div className="flex items-center">
                      <span>Completed</span>
                      {sortState.column === 'completed' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('name')}
                  >
                    <div className="flex items-center">
                      <span>Project Name</span>
                      {sortState.column === 'name' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('images')}
                  >
                    <div className="flex items-center">
                      <span>Images</span>
                      {sortState.column === 'images' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('progress')}
                  >
                    <div className="flex items-center">
                      <span>Your Progress</span>
                      {sortState.column === 'progress' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('qualified')}
                  >
                    <div className="flex items-center">
                      <span>Qualified</span>
                      {sortState.column === 'qualified' && (
                        <span className="ml-2">
                          {sortState.direction === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : sortState.direction === 'desc' ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : null}
                        </span>
                      )}
                    </div>
                  </th>
                  {user?.is_admin && (
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleColumnSort('qa_pending')}
                    >
                      <div className="flex items-center">
                        <span>Pending</span>
                        {sortState.column === 'qa_pending' && (
                          <span className="ml-2">
                            {sortState.direction === 'asc' ? (
                              <ChevronUpIcon className="w-4 h-4" />
                            ) : sortState.direction === 'desc' ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : null}
                          </span>
                        )}
                      </div>
                    </th>
                  )}
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getDisplayedProjects().map(project => {
                  const isCompleted = project.is_user_completed || false
                  const isQualified = project.is_qualified || false
                  const isSelected = selectedProjects.has(project.id)

                  return (
                    <tr
                      key={project.id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      {selectMode && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProjectSelection(project.id)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            onClick={e => e.stopPropagation()}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          status={project.user_status?.status || ProjectStatus.NOT_STARTED}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {isCompleted ? (
                            <div className="flex items-center">
                              <CheckCircleIconSolid className="w-5 h-5 text-green-600 mr-2" />
                              <span className="text-sm font-medium text-green-600">Yes</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <XCircleIcon className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-500">No</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{project.name}</div>
                          {project.description && (
                            <div className="text-sm text-gray-500">{project.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {project.total_images}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm text-gray-900">
                            {project.annotated_images_by_user} / {project.total_images}
                          </div>
                          <div className="ml-2 w-16">
                            <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                              <div
                                style={{
                                  width: `${
                                    project.total_images > 0
                                      ? (project.annotated_images_by_user / project.total_images) *
                                        100
                                      : 0
                                  }%`,
                                }}
                                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {isQualified ? (
                            <CheckCircleIconSolid className="w-5 h-5 text-green-600 mr-2" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-gray-400 mr-2" />
                          )}
                          <span className="text-sm text-gray-900">
                            {project.completed_by_count || 0} / 3
                          </span>
                        </div>
                      </td>
                      {user?.is_admin && (
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            (project.qa_pending_user_count ?? 0) > 0
                              ? 'text-red-600'
                              : 'text-gray-900'
                          }`}
                        >
                          {project.qa_pending_user_count ?? 0}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => navigate(`/projects/${project.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Open project"
                          >
                            <CursorArrowRippleIcon className="w-5 h-5" />
                          </button>
                          {user?.is_admin && (
                            <>
                              <button
                                onClick={() => {
                                  setAssignModalProject(project)
                                  setShowAssignModal(true)
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Assign users"
                              >
                                <UserPlusIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/analysis`)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Analyze project"
                              >
                                <ChartBarIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/review`)}
                                className="text-purple-600 hover:text-purple-900"
                                title="Review annotations"
                              >
                                <ClipboardDocumentCheckIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/projects/${project.id}/edit`)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Edit project"
                              >
                                <Cog6ToothIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProject(project.id, project.name)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete project"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <ProjectModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateProject} />
      )}

      {showBatchExportModal && (
        <BatchExportModal
          selectedProjectIds={Array.from(selectedProjects)}
          onClose={() => setShowBatchExportModal(false)}
          onExportComplete={() => {
            setShowBatchExportModal(false)
            // Optionally clear selection after export
            // clearSelection()
          }}
        />
      )}

      {showAssignModal && assignModalProject && (
        <AssignUsersModal
          project={assignModalProject}
          onClose={() => {
            setShowAssignModal(false)
            setAssignModalProject(null)
          }}
          onAssignComplete={() => {
            fetchMyProjectsData()
            fetchProjectsData()
          }}
        />
      )}
    </div>
  )
}
